// Pinterest Search tab component - search Pinterest pins

import { useState, useCallback, useEffect } from "react";
import styles from "./PinterestSearchTab.module.css";
import { apiUrl } from "../config";

interface PinterestPin {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  imageUrlLarge: string;
  imageUrlOrig: string;
  isVideo: boolean;
  videoUrl?: string;
  videoDuration?: number;
  videoThumbnail?: string;
  pinner: {
    username: string;
    fullName: string;
    avatar: string;
    followerCount: number;
  };
  board: {
    name: string;
    url: string;
    pinCount: number;
  };
  link?: string;
  domain?: string;
  dominantColor?: string;
  createdAt?: string;
  reactionCount: number;
  tags: string[];
}

interface PinterestSearchTabProps {
  onSelectPin?: (pin: PinterestPin) => void;
  onAddToPinJobs?: (pins: PinterestPin[]) => void;
  disabled?: boolean;
}

type SortType = "relevance" | "recent";

export function PinterestSearchTab({ onSelectPin, onAddToPinJobs, disabled }: PinterestSearchTabProps) {
  const [query, setQuery] = useState("");
  const [pins, setPins] = useState<PinterestPin[]>([]);
  const [selectedPins, setSelectedPins] = useState<PinterestPin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedPin, setExpandedPin] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortType>("relevance");
  const [numResults, setNumResults] = useState(30);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isAddingToJobs, setIsAddingToJobs] = useState(false);
  const [existingPinIds, setExistingPinIds] = useState<Set<string>>(new Set());
  const [hideUsedPins, setHideUsedPins] = useState(false);

  // Fetch existing pin job IDs to highlight already-used pins
  const fetchExistingPinIds = useCallback(async () => {
    try {
      const response = await fetch(apiUrl("/api/pin-jobs?limit=1000"));
      const data = await response.json();
      if (data.pinJobs) {
        const ids = new Set<string>(
          data.pinJobs.map((job: { original_pin_id: string }) => job.original_pin_id)
        );
        setExistingPinIds(ids);
      }
    } catch (err) {
      console.error("Failed to fetch existing pin IDs:", err);
    }
  }, []);

  // Fetch existing pin IDs on mount
  useEffect(() => {
    fetchExistingPinIds();
  }, [fetchExistingPinIds]);

  // Search function that can be called directly
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) return;

    setQuery(searchQuery);
    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    setNextCursor(null);

    try {
      const response = await fetch(apiUrl("/api/pinterest/search/advanced"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          keyword: searchQuery,
          num: numResults,
          sort: sortBy,
          cursor: null,
        }),
      });

      const text = await response.text();
      if (!text) {
        throw new Error("Empty response from server");
      }

      const data = JSON.parse(text);

      if (!response.ok) {
        throw new Error(data.message || "Search failed");
      }

      setPins(data.pins || []);
      setNextCursor(data.nextCursor || null);
    } catch (err) {
      console.error("Pinterest search error:", err);
      setError(err instanceof Error ? err.message : "Search failed");
      setPins([]);
    } finally {
      setIsLoading(false);
    }
  }, [numResults, sortBy]);

  // Check URL for pinterest_search param on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get("pinterest_search");
    if (searchParam) {
      performSearch(searchParam);
      // Clear the URL param without reloading
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [performSearch]);

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(query);
  }, [query, performSearch]);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return;

    setIsLoadingMore(true);
    setError(null);

    try {
      const response = await fetch(apiUrl("/api/pinterest/search/advanced"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          keyword: query,
          num: numResults,
          sort: sortBy,
          cursor: nextCursor,
        }),
      });

      const text = await response.text();
      if (!text) {
        throw new Error("Empty response from server");
      }

      const data = JSON.parse(text);

      if (!response.ok) {
        throw new Error(data.message || "Load more failed");
      }

      // Append new pins, avoiding duplicates
      const newPins = (data.pins || []).filter(
        (p: PinterestPin) => !pins.some(existing => existing.id === p.id)
      );
      setPins(prev => [...prev, ...newPins]);
      setNextCursor(data.nextCursor || null);
    } catch (err) {
      console.error("Load more error:", err);
      setError(err instanceof Error ? err.message : "Load more failed");
    } finally {
      setIsLoadingMore(false);
    }
  }, [query, numResults, sortBy, nextCursor, pins, isLoadingMore]);

  const handleToggleSelect = useCallback((pin: PinterestPin) => {
    setSelectedPins((prev) => {
      const isSelected = prev.some((p) => p.id === pin.id);
      if (isSelected) {
        return prev.filter((p) => p.id !== pin.id);
      } else {
        return [...prev, pin];
      }
    });
    if (onSelectPin) {
      onSelectPin(pin);
    }
  }, [onSelectPin]);

  const handleSelectAll = useCallback(() => {
    const allSelected = pins.every((p) =>
      selectedPins.some((sp) => sp.id === p.id)
    );

    if (allSelected) {
      setSelectedPins((prev) =>
        prev.filter((sp) => !pins.some((p) => p.id === sp.id))
      );
    } else {
      setSelectedPins((prev) => {
        const newPins = pins.filter(
          (p) => !prev.some((pp) => pp.id === p.id)
        );
        return [...prev, ...newPins];
      });
    }
  }, [pins, selectedPins]);

  const handleClearSelection = useCallback(() => {
    setSelectedPins([]);
  }, []);

  const handleAddToPinJobs = useCallback(async () => {
    if (selectedPins.length === 0) return;

    setIsAddingToJobs(true);
    setError(null);

    try {
      const pinsData = selectedPins.map((pin) => ({
        original_pin_id: pin.id,
        original_title: pin.title,
        original_description: pin.description,
        original_image_url: pin.imageUrl,
        original_image_url_large: pin.imageUrlOrig,
        original_pinner_username: pin.pinner.username,
        original_pinner_name: pin.pinner.fullName,
        original_board_name: pin.board.name,
        original_link: pin.link,
        original_domain: pin.domain,
        is_video: pin.isVideo,
        video_url: pin.videoUrl,
        tags: pin.tags,
      }));

      const response = await fetch(apiUrl("/api/pin-jobs"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pins: pinsData }),
      });

      const data = await response.json();

      if (response.ok) {
        const successCount = data.results?.filter((r: { success: boolean }) => r.success).length || 0;
        if (successCount > 0) {
          // Clear selection and notify parent
          setSelectedPins([]);
          if (onAddToPinJobs) {
            onAddToPinJobs(selectedPins);
          }
          // Refresh existing pin IDs to update highlights
          await fetchExistingPinIds();
          alert(`Added ${successCount} pins to Pin Jobs!`);
        } else {
          setError("Pins already exist in Pin Jobs");
        }
      } else {
        setError(data.error || "Failed to add to Pin Jobs");
      }
    } catch (err) {
      setError("Failed to add to Pin Jobs");
    } finally {
      setIsAddingToJobs(false);
    }
  }, [selectedPins, onAddToPinJobs, fetchExistingPinIds]);

  const isPinSelected = (pinId: string) =>
    selectedPins.some((p) => p.id === pinId);

  const isPinAlreadyUsed = (pinId: string) =>
    existingPinIds.has(pinId);

  // Filter pins based on hideUsedPins setting
  const displayedPins = hideUsedPins
    ? pins.filter((pin) => !existingPinIds.has(pin.id))
    : pins;

  const usedPinsCount = pins.filter((pin) => existingPinIds.has(pin.id)).length;

  const allCurrentSelected =
    pins.length > 0 &&
    pins.every((p) => selectedPins.some((sp) => sp.id === p.id));

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSearch} className={styles.searchForm}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search Pinterest for recipe ideas, food photography..."
          className={styles.searchInput}
          disabled={disabled || isLoading}
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortType)}
          className={styles.sortSelect}
          disabled={disabled || isLoading}
        >
          <option value="relevance">Relevance</option>
          <option value="recent">Recent</option>
        </select>
        <input
          type="number"
          value={numResults}
          onChange={(e) => setNumResults(Math.min(100, Math.max(1, parseInt(e.target.value) || 20)))}
          min={1}
          max={100}
          className={styles.numInput}
          disabled={disabled || isLoading}
          title="Number of results"
        />
        <button
          type="submit"
          className={styles.searchButton}
          disabled={disabled || isLoading || !query.trim()}
        >
          {isLoading ? "Searching..." : "Search"}
        </button>
      </form>

      {/* Selection bar */}
      {selectedPins.length > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectionCount}>
            {selectedPins.length} pin{selectedPins.length !== 1 ? "s" : ""} selected
          </span>
          <div className={styles.selectionActions}>
            <button
              onClick={handleClearSelection}
              className={styles.clearButton}
              disabled={disabled}
            >
              Clear All
            </button>
            <button
              onClick={() => {
                // Download selected images
                selectedPins.forEach((pin) => {
                  window.open(pin.imageUrlOrig, "_blank");
                });
              }}
              className={styles.downloadButton}
              disabled={disabled}
            >
              Open Images ({selectedPins.length})
            </button>
            <button
              onClick={handleAddToPinJobs}
              className={styles.addToJobsButton}
              disabled={disabled || isAddingToJobs}
            >
              {isAddingToJobs ? "Adding..." : `Add to Pin Jobs (${selectedPins.length})`}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      {isLoading && (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Searching Pinterest...</span>
        </div>
      )}

      {!isLoading && hasSearched && pins.length === 0 && !error && (
        <div className={styles.noResults}>
          No pins found. Try a different search term.
        </div>
      )}

      {!isLoading && pins.length > 0 && (
        <div className={styles.results}>
          <div className={styles.resultsHeader}>
            <p className={styles.resultsCount}>
              Found {pins.length} pins
              {usedPinsCount > 0 && (
                <span className={styles.usedCount}>
                  ({usedPinsCount} already used)
                </span>
              )}
            </p>
            <div className={styles.resultsActions}>
              <button
                onClick={() => setHideUsedPins(!hideUsedPins)}
                className={`${styles.hideUsedButton} ${hideUsedPins ? styles.active : ""}`}
                disabled={disabled || usedPinsCount === 0}
                title={usedPinsCount > 0 ? `${usedPinsCount} pins already in Pin Jobs` : "No used pins found"}
              >
                {hideUsedPins ? `Show Used (${usedPinsCount})` : `Hide Used (${usedPinsCount})`}
              </button>
              <button
                onClick={handleSelectAll}
                className={styles.selectAllButton}
                disabled={disabled}
              >
                {allCurrentSelected ? "Deselect All" : "Select All"}
              </button>
            </div>
          </div>
          <div className={styles.grid}>
            {displayedPins.map((pin) => (
              <div
                key={pin.id}
                className={`${styles.pinCard} ${isPinSelected(pin.id) ? styles.selected : ""} ${isPinAlreadyUsed(pin.id) ? styles.alreadyUsed : ""}`}
                onClick={() => handleToggleSelect(pin)}
                style={{
                  borderColor: isPinSelected(pin.id) ? "#e91e63" : isPinAlreadyUsed(pin.id) ? "#f59e0b" : "transparent",
                  backgroundColor: pin.dominantColor ? `${pin.dominantColor}22` : undefined
                }}
              >
                <div className={styles.pinImageWrapper}>
                  <img
                    src={pin.imageUrl}
                    alt={pin.title}
                    className={styles.pinImage}
                    loading="lazy"
                  />
                  {isPinAlreadyUsed(pin.id) && (
                    <div className={styles.usedBadge}>
                      Already Used
                    </div>
                  )}
                  {pin.isVideo && (
                    <div className={styles.videoBadge}>
                      {pin.videoDuration ? formatDuration(pin.videoDuration) : "Video"}
                    </div>
                  )}
                  {isPinSelected(pin.id) && (
                    <div className={styles.selectedOverlay}>
                      <span className={styles.checkmark}>✓</span>
                    </div>
                  )}
                  <div className={styles.pinActions}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(pin.imageUrlOrig, "_blank");
                      }}
                      className={styles.actionBtn}
                      title="Open full image"
                    >
                      🔍
                    </button>
                    {pin.link && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(pin.link, "_blank");
                        }}
                        className={styles.actionBtn}
                        title="Open source link"
                      >
                        🔗
                      </button>
                    )}
                  </div>
                </div>
                <div className={styles.pinInfo}>
                  <h3 className={styles.pinTitle}>
                    {pin.title || "Untitled"}
                  </h3>
                  {pin.description && pin.description !== " " && (
                    <p className={styles.pinDesc}>
                      {pin.description.slice(0, 100)}{pin.description.length > 100 ? "..." : ""}
                    </p>
                  )}
                  <div className={styles.pinMeta}>
                    <div className={styles.pinnerInfo}>
                      {pin.pinner.avatar && (
                        <img
                          src={pin.pinner.avatar}
                          alt={pin.pinner.username}
                          className={styles.pinnerAvatar}
                        />
                      )}
                      <span className={styles.pinnerName}>@{pin.pinner.username}</span>
                    </div>
                    <div className={styles.pinStats}>
                      {pin.reactionCount > 0 && (
                        <span className={styles.stat}>❤️ {formatNumber(pin.reactionCount)}</span>
                      )}
                    </div>
                  </div>
                  {pin.board && (
                    <div className={styles.boardInfo}>
                      📌 {pin.board.name}
                    </div>
                  )}
                  {pin.tags.length > 0 && (
                    <div className={styles.tags}>
                      {pin.tags.slice(0, 3).map((tag) => (
                        <button
                          key={tag}
                          className={styles.tag}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Open new tab with Pinterest search for this tag
                            const searchUrl = `${window.location.origin}${window.location.pathname}?pinterest_search=${encodeURIComponent(tag)}`;
                            window.open(searchUrl, "_blank");
                          }}
                          title={`Search for "${tag}"`}
                        >
                          {tag}
                        </button>
                      ))}
                      {pin.tags.length > 3 && (
                        <span className={styles.moreTagsLabel}>+{pin.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                  {pin.domain && pin.domain !== "Uploaded by user" && (
                    <div className={styles.domain}>
                      {pin.domain}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {nextCursor && (
            <div className={styles.loadMoreContainer}>
              <button
                onClick={handleLoadMore}
                className={styles.loadMoreButton}
                disabled={disabled || isLoadingMore}
              >
                {isLoadingMore ? "Loading..." : "Load More Pins"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
